import React, { useState, useEffect, useCallback } from 'react';
import { API_URL } from '../../config';
import { useAuth } from '../../context/AuthContext';

interface Comment {
    id: number;
    content: string;
    created_at: string;
    user_name: string;
    is_mine: boolean;
}

interface TradingFeedbackProps {
    decisionId: number;
    initialLikes: number;
    initialHasLiked: boolean;
    initialCommentsCount: number;
}

const TradingFeedback: React.FC<TradingFeedbackProps> = ({
    decisionId,
    initialLikes,
    initialHasLiked,
    initialCommentsCount
}) => {
    const { token } = useAuth();
    const [likes, setLikes] = useState(initialLikes);
    const [hasLiked, setHasLiked] = useState(initialHasLiked);
    const [comments, setComments] = useState<Comment[]>([]);
    const [commentsCount, setCommentsCount] = useState(initialCommentsCount);
    const [newComment, setNewComment] = useState('');
    const [loading, setLoading] = useState(false);
    const [showComments, setShowComments] = useState(false);
    const commentsEndRef = React.useRef<HTMLDivElement>(null);

    const scrollToBottom = () => {
        commentsEndRef.current?.scrollIntoView({ behavior: "smooth" });
    };

    useEffect(() => {
        if (showComments && comments.length > 0) {
            scrollToBottom();
        }
    }, [comments, showComments]);

    const fetchFeedback = useCallback(async () => {
        if (!token) return;
        try {
            const res = await fetch(`${API_URL}/api/ai-portfolio/decisions/${decisionId}/feedback`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (res.ok) {
                const data = await res.json();
                setLikes(data.likes_count);
                setHasLiked(data.has_liked);
                setComments(data.comments);
                setCommentsCount(data.comments.length);
            }
        } catch (e) { console.error("Error fetching feedback", e); }
    }, [token, decisionId]);

    useEffect(() => {
        if (showComments) {
            fetchFeedback();
        }
    }, [showComments, fetchFeedback]);

    const handleLike = async (e: React.MouseEvent) => {
        e.stopPropagation();
        if (!token) return;
        try {
            const res = await fetch(`${API_URL}/api/ai-portfolio/decisions/${decisionId}/like`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (res.ok) {
                const data = await res.json();
                setHasLiked(data.liked);
                setLikes(data.count);
            }
        } catch (e) { console.error("Error liking", e); }
    };

    const handleSubmitComment = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!token || !newComment.trim() || loading) return;

        setLoading(true);
        try {
            const res = await fetch(`${API_URL}/api/ai-portfolio/decisions/${decisionId}/comment`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ content: newComment })
            });
            if (res.ok) {
                setNewComment('');
                fetchFeedback();
            }
        } catch (e) { console.error("Error commenting", e); }
        finally { setLoading(false); }
    };

    return (
        <div style={{ marginTop: 15, borderTop: '1px solid var(--border-color)', paddingTop: 10 }}>
            <style>{`
                .custom-scrollbar::-webkit-scrollbar {
                    width: 6px;
                }
                .custom-scrollbar::-webkit-scrollbar-track {
                    background: transparent;
                }
                .custom-scrollbar::-webkit-scrollbar-thumb {
                    background: #e2e8f0;
                    border-radius: 10px;
                }
                .custom-scrollbar::-webkit-scrollbar-thumb:hover {
                    background: #cbd5e1;
                }
                .fb-button {
                    background: none;
                    border: none;
                    color: var(--text-muted);
                    cursor: pointer;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    gap: 8px;
                    fontWeight: 600;
                    fontSize: 14px;
                    padding: 8px 12px;
                    borderRadius: 6px;
                    transition: background 0.2s;
                    flex: 1;
                }
                .fb-button:hover {
                    background-color: var(--bg-input);
                }
                .fb-button.active {
                    color: #1877f2;
                }
                @keyframes fadeIn {
                    from { opacity: 0; transform: translateY(5px); }
                    to { opacity: 1; transform: translateY(0); }
                }
            `}</style>

            {/* Stats Bar */}
            {(likes > 0 || commentsCount > 0) && (
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingBottom: 10, marginBottom: 4 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: 'var(--text-muted)' }}>
                        {likes > 0 && (
                            <div style={{ display: 'flex', alignItems: 'center' }}>
                                <span style={{
                                    backgroundColor: '#1877f2',
                                    color: 'white',
                                    borderRadius: '50%',
                                    width: 18,
                                    height: 18,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontSize: 10,
                                    marginRight: 4
                                }}>👍</span>
                                {likes}
                            </div>
                        )}
                    </div>
                    {commentsCount > 0 && (
                        <div
                            style={{ fontSize: 13, color: 'var(--text-muted)', cursor: 'pointer' }}
                            onClick={(e) => { e.stopPropagation(); setShowComments(!showComments); }}
                        >
                            {commentsCount} commentaire{commentsCount > 1 ? 's' : ''}
                        </div>
                    )}
                </div>
            )}

            {/* Separator */}
            <div style={{ height: '1px', backgroundColor: 'var(--border-color)', margin: '0 -4px' }}></div>

            {/* Action Bar */}
            <div style={{ display: 'flex', alignItems: 'center', padding: '4px 0' }}>
                <button
                    onClick={handleLike}
                    className={`fb-button ${hasLiked ? 'active' : ''}`}
                >
                    <span style={{ fontSize: 18 }}>{hasLiked ? '👍' : '👍'}</span>
                    J'aime
                </button>

                <button
                    onClick={(e) => { e.stopPropagation(); setShowComments(!showComments); }}
                    className="fb-button"
                >
                    <span style={{ fontSize: 18 }}>💬</span>
                    Commenter
                </button>
            </div>

            {/* Comments Section */}
            {showComments && (
                <div style={{ marginTop: 15, animation: 'fadeIn 0.3s ease' }} onClick={(e) => e.stopPropagation()}>
                    {/* Comment List */}
                    <div className="custom-scrollbar" style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 16,
                        marginBottom: 20,
                        maxHeight: 350,
                        overflowY: 'auto',
                        paddingRight: 10,
                        paddingLeft: 2
                    }}>
                        {comments.length === 0 ? (
                            <div style={{ fontSize: 13, color: 'var(--text-muted)', fontStyle: 'italic', textAlign: 'center', padding: '20px 0' }}>
                                Soyez le premier à commenter cette décision.
                            </div>
                        ) : comments.map(c => (
                            <div key={c.id} style={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 6,
                                alignSelf: c.is_mine ? 'flex-end' : 'flex-start',
                                maxWidth: '85%',
                                alignItems: c.is_mine ? 'flex-end' : 'flex-start'
                            }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexDirection: c.is_mine ? 'row-reverse' : 'row' }}>
                                    <span style={{ fontSize: 11, fontWeight: '700', color: c.is_mine ? '#6366f1' : '#7c3aed' }}>{c.user_name}</span>
                                    <span style={{ fontSize: 10, color: 'var(--text-muted)', opacity: 0.8 }}>
                                        {new Date(c.created_at).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                                    </span>
                                </div>
                                <div style={{
                                    backgroundColor: c.is_mine ? '#6366f1' : 'var(--bg-input)',
                                    color: c.is_mine ? '#ffffff' : 'var(--text-main)',
                                    padding: '10px 16px',
                                    borderRadius: c.is_mine ? '18px 18px 2px 18px' : '18px 18px 18px 2px',
                                    fontSize: 14,
                                    lineHeight: '1.5',
                                    border: c.is_mine ? 'none' : '1px solid var(--border-color)',
                                    boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
                                    wordBreak: 'break-word'
                                }}>
                                    {c.content}
                                </div>
                            </div>
                        ))}
                        <div ref={commentsEndRef} />
                    </div>

                    {/* New Comment Input */}
                    <form onSubmit={handleSubmitComment} style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                        <input
                            type="text"
                            placeholder="Écrire un commentaire..."
                            value={newComment}
                            onChange={(e) => setNewComment(e.target.value)}
                            style={{
                                flex: 1,
                                padding: '10px 15px',
                                borderRadius: 20,
                                border: '1px solid var(--border-color)',
                                backgroundColor: 'var(--bg-input)',
                                color: 'var(--text-main)',
                                fontSize: 13,
                                outline: 'none'
                            }}
                        />
                        <button
                            type="submit"
                            disabled={!newComment.trim() || loading}
                            style={{
                                background: 'none',
                                border: 'none',
                                color: '#7c3aed',
                                cursor: (newComment.trim() && !loading) ? 'pointer' : 'default',
                                opacity: (newComment.trim() && !loading) ? 1 : 0.5,
                                fontSize: 18
                            }}
                        >
                            ✈️
                        </button>
                    </form>
                </div>
            )}
        </div>
    );
};

export default TradingFeedback;
